import { createSelector, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { RootState } from '../services/store';

import { PURGE } from 'redux-persist';

import { LibraryState } from '../types/library';
import { selectSharedById } from '../services/share';
import { SortDirection, SortType } from '../types/common';

const initialState: LibraryState = {
  page: 0,
  options: {
    sortType: SortType.SHARE_DATE,
    sortOrder: SortDirection.DESC,
    layoutType: 'grid',
    isEditMode: false,
    isDetailsOpened: false,
  },
  selectedIds: [],
  selectedItem: null,
  links: [],
};

export const shareSlice = createSlice({
  name: 'share',
  initialState,
  reducers: {
    sharePageChanged: (state, action) => {
      state.page = action.payload;
    },
    shareSortTypeChanged: (state, action) => {
      state.options.sortType = action.payload;
    },
    shareOrderChanged: (
      state,
      action: PayloadAction<SortDirection, string>
    ) => {
      state.options.sortOrder = action.payload;
    },
    shareLayoutTypeChanged: (state, action) => {
      state.options.layoutType = action.payload;
    },
    shareEditModeToggled: (state, action) => {
      state.options.isEditMode = action.payload;
    },
    shareDetailsToggled: (
      state,
      action: PayloadAction<boolean | undefined, string>
    ) => {
      state.options.isDetailsOpened =
        action.payload ?? !state.options.isDetailsOpened;
    },
    shareItemSelected: (state, action) => {
      state.selectedIds = action.payload.ids;
      if (action.payload.item !== undefined) {
        state.selectedItem = action.payload.item;
      }
    },
    shareIndexListChanged: (state, action) => {
      if (state.selectedItem) {
        state.selectedItem.projectIndex.indexList = action.payload;
      }
    },
    shareIndexIndentChanged: (state, action) => {
      const item = state.selectedItem?.projectIndex.indexList[action.payload];
      if (item) {
        item.indentYn = item.indentYn ? 0 : 1;
      }
    },
    shareHistoryChanged: (state, action) => {
      if (state.selectedItem)
        state.selectedItem.projectHistoryList = action.payload;
    },
    shareLinkNotiSet: (state, action) => {
      state.links = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(PURGE, () => initialState);
  },
});

export const {
  sharePageChanged,
  shareSortTypeChanged,
  shareOrderChanged,
  shareLayoutTypeChanged,
  shareEditModeToggled,
  shareDetailsToggled,
  shareItemSelected,
  shareIndexListChanged,
  shareIndexIndentChanged,
  shareHistoryChanged,
  shareLinkNotiSet,
} = shareSlice.actions;

export const selectSharePage = (state: RootState) => state.share.page;

export const selectShareOptions = (state: RootState) => state.share.options;

export const selectShareSelectedIds = (state: RootState) =>
  state.share.selectedIds;

export const selectShareSelectedId = createSelector(
  selectShareSelectedIds,
  (selectedIds) => selectedIds[0]
);

export const selectShareSelectedItems = (state: RootState) =>
  state.share.selectedIds.map((id) => selectSharedById(state, id));

export const selectShareSingleItem = createSelector(
  selectShareSelectedItems,
  (selectedItems) => selectedItems[0]
);

export const selectShareSelectedItem = (state: RootState) =>
  state.share.selectedItem;

export const selectShareProject = createSelector(
  selectShareSelectedItem,
  (selectedItem) => selectedItem?.project
);

export const selectresourceShare = createSelector(
  selectShareSelectedItem,
  (selectedItem) => selectedItem?.resourceShare
);

export const selectProjectPublic = createSelector(
  selectShareSelectedItem,
  (selectedItem) => selectedItem?.projectPublic
);

export const selectProjectHistoryList = createSelector(
  selectShareSelectedItem,
  (selectedItem) => selectedItem?.projectHistoryList
);

export const selectShareLinks = (state: RootState) => state.share.links;

export default shareSlice.reducer;
